import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Sparkles, Check, Crown, Building, Zap, ChefHat, Utensils, Coffee, Wine, Wheat, Truck, Pizza, CupSoda, Hotel, BedDouble, Plus, Trash2, PartyPopper, Loader2, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useAppContext } from "@/lib/onboardingContext";
import { useAuth } from "@/lib/authContext";
import { supabase } from "@/integrations/supabase/client";
import { LOCATION_DATA, getCitiesForCountry, getPlaceholderForCountry } from "@/lib/locationData";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useI18n } from "@/lib/i18nContext";
import { getCurrencySymbol, convertFromUSD } from "@/lib/currency";

// Step indices: 0=Welcome, 1=Goal, 2=Business info, 3=Seating/Rooms,
// 4=Hotel outlets (hotel only), 5=Plan.
// "Managing an existing business" skips opening-specific steps and jumps
// straight from business info to the operations dashboard.
const FULL_STEPS_BASE = 5;          // welcome + goal + business + seating + plan
const FULL_STEPS_HOTEL = 6;         // + outlets step for hotels
const MANAGING_STEPS = 3;           // welcome + goal + business info

const OUTLET_TYPE_OPTIONS = [
  "Restaurant",
  "Bar",
  "Café",
  "Room Service",
  "Banqueting",
  "Spa",
  "Pool Bar",
  "Lobby Lounge",
] as const;

const businessTypeOptions: Array<{ id: string; label: string; icon: LucideIcon }> = [
  { id: "restaurant", label: "Restaurant", icon: Utensils },
  { id: "coffee-shop", label: "Café", icon: Coffee },
  { id: "bar", label: "Bar", icon: Wine },
  { id: "bakery", label: "Bakery", icon: Wheat },
  { id: "food-truck", label: "Food Truck", icon: Truck },
  { id: "ghost-kitchen", label: "Virtual Kitchen", icon: ChefHat },
  { id: "pizzeria", label: "Pizzeria", icon: Pizza },
  { id: "juice-bar", label: "Juice Bar", icon: CupSoda },
  { id: "hotel", label: "Hotel", icon: Hotel },
  { id: "bed-and-breakfast", label: "Bed & Breakfast", icon: BedDouble },
  { id: "private-events", label: "Private Events & Catering", icon: PartyPopper },
  { id: "private-chef", label: "Private Chef", icon: ChefHat },
];

const goalOptions = [
  {
    id: "opening",
    label: "Opening a new business",
    desc: "I'm planning to launch and need a step-by-step roadmap to get there.",
  },
  {
    id: "managing",
    label: "Managing an existing business",
    desc: "Optimize operations, track performance, and stay compliant.",
  },
];

// Hotel / B&B room types — used when the venue is room-driven instead of
// seat-driven. Order roughly mirrors price tier, lowest → highest.
// `defaultAdrUSD` is a USD baseline that gets converted to the city's currency
// so users see a sensible starting price they can adjust.
const ROOM_TYPE_OPTIONS: { type: string; defaultAdrUSD: number }[] = [
  { type: "Standard", defaultAdrUSD: 120 },
  { type: "Superior", defaultAdrUSD: 160 },
  { type: "Deluxe", defaultAdrUSD: 220 },
  { type: "Junior Suite", defaultAdrUSD: 320 },
  { type: "Suite", defaultAdrUSD: 480 },
  { type: "Presidential Suite", defaultAdrUSD: 1200 },
];
const DEFAULT_OCCUPANCY = 65;

const planOptions = [
  { key: "free", name: "Free", price: 0, icon: Zap, features: ["1 opening roadmap", "Core permit checklist", "5 AI advisor questions"], popular: false },
  { key: "pro", name: "Pro", price: 99, icon: Crown, features: ["10 roadmaps", "Unlimited AI advisor", "Full operations suite"], popular: true },
  { key: "studio", name: "Studio", price: 179, icon: Building, features: ["25 roadmaps", "Team collaboration", "White-label exports"], popular: false },
];

export default function Onboarding() {
  const [step, setStep] = useState(0);
  const [selectedCountry, setSelectedCountry] = useState("");
  const navigate = useNavigate();
  const { onboarding, setOnboarding, generateResults } = useAppContext();
  const { user } = useAuth();
  const { t } = useI18n();

  // Post-onboarding completion screen state.
  // `completed` shows the "Your dashboard is ready" celebration before we
  // navigate to the dashboard (or kick off Stripe Checkout for paid plans).
  type CompletionMode = "managing" | "opening" | "checkout";
  const [completed, setCompleted] = useState<null | {
    mode: CompletionMode;
    title: string;
    subtitle: string;
    summary: { label: string; value: string }[];
    proceed: () => void;
  }>(null);
  const [showValidationHint, setShowValidationHint] = useState(false);

  // Auto-advance from the completion screen after a short delay so users see
  // the celebration but aren't stuck waiting.
  useEffect(() => {
    if (!completed) return;
    const t = setTimeout(() => completed.proceed(), 2400);
    return () => clearTimeout(t);
  }, [completed]);

  // Reset the "fill required fields" hint whenever the user changes step.
  useEffect(() => {
    setShowValidationHint(false);
  }, [step]);

  const update = (field: string, value: any) => setOnboarding(prev => ({ ...prev, [field]: value }));

  // Hotel & B&B use a room-driven model instead of seats.
  const isRoomDriven = onboarding.businessType === "hotel" || onboarding.businessType === "bed-and-breakfast";

  const totalCapacity =
    (Number(onboarding.indoorSeats) || 0) +
    (Number(onboarding.outdoorSeats) || 0) +
    (Number(onboarding.barSeats) || 0);

  // Hotel & B&B currency-aware helpers — ADR & projection display in local currency.
  const cs = getCurrencySymbol(onboarding.city || "");

  type GuestRoom = { type: string; quantity: number; adr: number; occupancy: number };
  const guestRooms: GuestRoom[] = (onboarding.guestRooms ?? []) as GuestRoom[];
  const totalRooms = guestRooms.reduce((sum, r) => sum + (Number(r.quantity) || 0), 0);

  // Monthly room revenue = Σ rooms × ADR × occupancy × 30 days, by type.
  const projectedMonthlyRoomRevenue = guestRooms.reduce((sum, r) => {
    const qty = Number(r.quantity) || 0;
    const adr = Number(r.adr) || 0;
    const occ = (Number(r.occupancy) || 0) / 100;
    return sum + qty * adr * occ * 30;
  }, 0);

  const setGuestRooms = (rooms: GuestRoom[]) => {
    update("guestRooms", rooms);
  };
  const addRoomType = () => {
    const used = new Set(guestRooms.map(r => r.type));
    const nextDef = ROOM_TYPE_OPTIONS.find(t => !used.has(t.type)) ?? ROOM_TYPE_OPTIONS[0];
    const localAdr = convertFromUSD(nextDef.defaultAdrUSD, onboarding.city || "");
    setGuestRooms([
      ...guestRooms,
      { type: nextDef.type, quantity: 1, adr: localAdr, occupancy: DEFAULT_OCCUPANCY },
    ]);
  };
  const updateRoom = (idx: number, patch: Partial<GuestRoom>) => {
    setGuestRooms(guestRooms.map((r, i) => {
      if (i !== idx) return r;
      const merged = { ...r, ...patch };
      // When the user switches type, refresh the ADR default — but only if
      // they hadn't explicitly customised it (i.e. it still matches the prior
      // type's default).
      if (patch.type && patch.type !== r.type) {
        const prevDef = ROOM_TYPE_OPTIONS.find(t => t.type === r.type);
        const newDef = ROOM_TYPE_OPTIONS.find(t => t.type === patch.type);
        const prevDefaultLocal = prevDef
          ? convertFromUSD(prevDef.defaultAdrUSD, onboarding.city || "")
          : null;
        if (newDef && (r.adr === 0 || r.adr === prevDefaultLocal)) {
          merged.adr = convertFromUSD(newDef.defaultAdrUSD, onboarding.city || "");
        }
      }
      return merged;
    }));
  };
  const removeRoom = (idx: number) => {
    setGuestRooms(guestRooms.filter((_, i) => i !== idx));
  };

  // Hotel outlets — only shown for the "hotel" business type.
  const hasOutletsStep = onboarding.businessType === "hotel";
  type Outlet = { name: string; type: string; seats?: number };
  const outlets: Outlet[] = (onboarding.outlets ?? []) as Outlet[];
  const totalOutletCovers = outlets.reduce((sum, o) => sum + (Number(o.seats) || 0), 0);
  const setOutlets = (next: Outlet[]) => update("outlets", next);
  const addOutlet = () => {
    setOutlets([...outlets, { name: "", type: OUTLET_TYPE_OPTIONS[0], seats: 0 }]);
  };
  const updateOutlet = (idx: number, patch: Partial<Outlet>) => {
    setOutlets(outlets.map((o, i) => (i === idx ? { ...o, ...patch } : o)));
  };
  const removeOutlet = (idx: number) => {
    setOutlets(outlets.filter((_, i) => i !== idx));
  };

  const isManaging = onboarding.goal === "managing";
  // Plan step lives at index 4 normally, or 5 for hotels (after outlets step).
  const FULL_STEPS = hasOutletsStep ? FULL_STEPS_HOTEL : FULL_STEPS_BASE;
  const totalSteps = isManaging ? MANAGING_STEPS : FULL_STEPS;
  const displayStep = step;

  const canNext = (): boolean => {
    switch (step) {
      case 0: return true; // welcome
      case 1: return !!onboarding.goal; // goal selection
      case 2: {
        const baseOk =
          onboarding.businessName.trim().length > 0 &&
          !!onboarding.businessType &&
          onboarding.city.trim().length > 0;
        if (!baseOk) return false;
        if (isManaging) {
          return (
            !!onboarding.monthsOpen &&
            (Number(onboarding.seats) || 0) > 0 &&
            (Number(onboarding.monthlyRevenueEstimate) || 0) > 0 &&
            !!onboarding.biggestChallenge
          );
        }
        return true;
      }
      case 3: return isRoomDriven ? totalRooms > 0 : totalCapacity > 0; // seating / rooms
      case 4:
        // For hotels, step 4 is the outlets step — require at least one named outlet.
        if (hasOutletsStep) {
          return outlets.length > 0 && outlets.every(o => o.name.trim().length > 0 && !!o.type);
        }
        return !!onboarding.selectedPlan;
      case 5: return !!onboarding.selectedPlan;
      default: return true;
    }
  };

  const handleFinish = async () => {
    // Save location to DB
    if (user) {
      const locationPayload: Record<string, any> = {
        user_id: user.id,
        name: onboarding.businessName.trim(),
        business_type: onboarding.businessType,
        city: onboarding.city.trim(),
        country: onboarding.country || null,
      };
      if (isManaging) {
        locationPayload.months_open = onboarding.monthsOpen || null;
        locationPayload.seats_count = Number(onboarding.seats) || null;
        locationPayload.monthly_revenue_estimate = Number(onboarding.monthlyRevenueEstimate) || null;
        locationPayload.biggest_challenge = onboarding.biggestChallenge || null;
      }
      await (supabase as any).from("locations").insert(locationPayload);

      // Persist the goal on the user's profile so dashboard routing picks the
      // correct experience (opening vs managing) on every device.
      await (supabase as any)
        .from("profiles")
        .update({ goal: onboarding.goal })
        .eq("user_id", user.id);
    }
    // Generate results with defaults for dashboard compatibility
    generateResults();

    // Friendly business-type label for the summary screen.
    const typeLabel =
      businessTypeOptions.find(b => b.id === onboarding.businessType)?.label ||
      onboarding.businessType ||
      "Business";
    const cityLabel = (onboarding.city || "").trim() || "Your city";

    // ── Managing flow → Operations Dashboard ──
    if (isManaging) {
      setCompleted({
        mode: "managing",
        title: "Welcome to your Operations Dashboard",
        subtitle: `${onboarding.businessName.trim() || "Your business"} is ready to track.`,
        summary: [
          { label: "Business", value: onboarding.businessName.trim() || "—" },
          { label: "Type", value: typeLabel },
          { label: "City", value: cityLabel },
          ...(onboarding.monthsOpen ? [{ label: "Open for", value: onboarding.monthsOpen }] : []),
          ...(onboarding.biggestChallenge ? [{ label: "Focus", value: onboarding.biggestChallenge }] : []),
        ],
        proceed: () => navigate("/dashboard/operations"),
      });
      return;
    }

    // ── Opening flow ──
    const selected = onboarding.selectedPlan as string | undefined;
    const paidPlans: Record<string, "pro" | "business"> = {
      pro: "pro",
      studio: "business",
    };
    const backendPlan = selected ? paidPlans[selected] : undefined;

    const summaryItems = [
      { label: "Concept", value: typeLabel },
      { label: "City", value: cityLabel },
      ...(isRoomDriven
        ? [{ label: "Rooms", value: String(totalRooms) }]
        : [{ label: "Seats", value: String(totalCapacity) }]),
      { label: "Plan", value: planOptions.find(p => p.key === selected)?.name || "Free" },
    ];

    if (backendPlan) {
      // Paid plan → show celebration briefly, then redirect to Stripe Checkout.
      setCompleted({
        mode: "checkout",
        title: "Your Opening Roadmap is Ready",
        subtitle: `Sending you to checkout for ${cityLabel}…`,
        summary: summaryItems,
        proceed: async () => {
          try {
            const { data, error } = await supabase.functions.invoke("create-checkout", {
              body: { plan: backendPlan, billing: "monthly", origin: window.location.origin },
            });
            if (!error && data?.url) {
              window.location.href = data.url as string;
              return;
            }
            console.error("create-checkout failed", error);
          } catch (err) {
            console.error("create-checkout error", err);
          }
          navigate("/dashboard");
        },
      });
      return;
    }

    setCompleted({
      mode: "opening",
      title: "Your Opening Roadmap is Ready",
      subtitle: `${typeLabel} in ${cityLabel} — let's get you open.`,
      summary: summaryItems,
      proceed: () => navigate("/dashboard"),
    });
  };

  const next = () => {
    // "Managing" path: finish straight after the business-info step (step 2),
    // skipping the opening-specific seating + plan steps.
    if (step === 2 && isManaging) {
      handleFinish();
      return;
    }
    if (step < FULL_STEPS - 1) {
      setStep(step + 1);
    } else {
      handleFinish();
    }
  };

  const prev = () => {
    if (step > 0) setStep(step - 1);
    else navigate("/");
  };

  const progress = ((displayStep + 1) / totalSteps) * 100;

  const slide = {
    enter: { opacity: 0, x: 30, scale: 0.98 },
    center: { opacity: 1, x: 0, scale: 1 },
    exit: { opacity: 0, x: -30, scale: 0.98 },
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="container py-5 flex items-center justify-between">
        <span className="font-heading text-xl font-medium tracking-tight text-foreground">
          oplif<span className="text-primary">.</span>
        </span>
        {!completed && (
          <span className="text-sm text-muted-foreground tabular-nums">
            Step {displayStep + 1} of {totalSteps}
          </span>
        )}
      </div>

      {/* Numbered step indicator + progress bar */}
      {!completed && (
        <div className="container space-y-2.5">
          <div className="flex items-center gap-1.5 sm:gap-2">
            {Array.from({ length: totalSteps }).map((_, i) => {
              const state =
                i < displayStep ? "done" : i === displayStep ? "current" : "todo";
              return (
                <div key={i} className="flex items-center gap-1.5 sm:gap-2 flex-1 min-w-0">
                  <div
                    className={cn(
                      "shrink-0 w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-[11px] sm:text-xs font-medium transition-colors",
                      state === "done" && "bg-primary text-primary-foreground",
                      state === "current" && "bg-primary/15 text-primary ring-2 ring-primary/30",
                      state === "todo" && "bg-muted text-muted-foreground/70",
                    )}
                    aria-current={state === "current" ? "step" : undefined}
                  >
                    {state === "done" ? <Check className="w-3.5 h-3.5" /> : i + 1}
                  </div>
                  {i < totalSteps - 1 && (
                    <div className="flex-1 h-px bg-border" aria-hidden="true" />
                  )}
                </div>
              );
            })}
          </div>
          <div className="relative h-1 bg-muted rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-primary to-primary/80 rounded-full"
              initial={false}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            />
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 container flex items-center justify-center py-6 sm:py-10 px-4">
        <div className="w-full max-w-lg">
          <AnimatePresence mode="wait">
            {completed ? (
              <motion.div
                key="completed"
                initial={{ opacity: 0, scale: 0.96, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="text-center"
              >
                <motion.div
                  initial={{ scale: 0.6, rotate: -15, opacity: 0 }}
                  animate={{ scale: 1, rotate: 0, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.1 }}
                  className="mx-auto w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center"
                >
                  <Check className="w-10 h-10 text-primary" strokeWidth={2.5} />
                </motion.div>
                <h1 className="mt-6 font-heading text-2xl sm:text-3xl font-medium text-foreground">
                  {completed.title}
                </h1>
                <p className="mt-2 text-muted-foreground text-sm sm:text-base">
                  {completed.subtitle}
                </p>
                <p className="mt-4 text-xs uppercase tracking-wider text-muted-foreground/80 font-medium">
                  Your dashboard is ready
                </p>
                <div className="mt-6 rounded-2xl border border-border bg-card p-5 text-left shadow-sm">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-3">
                    Set up for you
                  </div>
                  <ul className="space-y-2.5">
                    {completed.summary.map((s) => (
                      <li key={s.label} className="flex items-start justify-between gap-3 text-sm">
                        <span className="text-muted-foreground">{s.label}</span>
                        <span className="font-medium text-foreground text-right truncate max-w-[60%]">
                          {s.value}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-6 flex items-center justify-center gap-3">
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">
                    {completed.mode === "checkout"
                      ? "Redirecting to checkout…"
                      : "Opening your dashboard…"}
                  </span>
                </div>
                <Button
                  onClick={completed.proceed}
                  className="mt-5 gap-2 shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] px-8"
                >
                  {completed.mode === "managing"
                    ? "Go to Operations Dashboard"
                    : completed.mode === "checkout"
                      ? "Continue to checkout"
                      : "Open my roadmap"}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </motion.div>
            ) : (
            <motion.div key={step} variants={slide} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3, ease: "easeOut" }}>

              {/* Step 0 — Welcome */}
              {step === 0 && (
                <div className="text-center space-y-6">
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 200 }}
                    className="mx-auto w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center"
                  >
                    <Sparkles className="w-10 h-10 text-primary" />
                  </motion.div>
                  <div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-medium text-foreground">
                      {t("onboarding.welcome")}<span className="text-primary"></span>
                    </h1>
                    <p className="mt-3 text-muted-foreground text-lg max-w-md mx-auto">
                      {t("onboarding.welcomeDesc")}
                    </p>
                  </div>
                  <p className="text-sm text-muted-foreground/70">
                    {t("onboarding.personalize")}
                  </p>
                </div>
              )}

              {/* Step 2 — Business info */}
              {step === 2 && (
                <div>
                  <h2 className="font-heading text-2xl sm:text-3xl font-medium text-foreground">{t("onboarding.tellUs")}</h2>
                  <p className="mt-2 text-muted-foreground text-sm">{t("onboarding.tellUsDesc")}</p>
                  <div className="mt-8 space-y-5">
                    <div>
                      <Label className="text-sm font-medium text-foreground">{t("onboarding.businessName")}</Label>
                      <Input
                        placeholder={t("onboarding.businessNamePlaceholder")}
                        className="mt-1.5 h-11"
                        value={onboarding.businessName}
                        onChange={e => update("businessName", e.target.value)}
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-foreground">{t("onboarding.businessType")}</Label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1.5 max-h-64 overflow-y-auto">
                        {businessTypeOptions.map(b => (
                          <button
                            key={b.id}
                            type="button"
                            onClick={() => update("businessType", b.id)}
                            className={cn(
                              "flex flex-col items-center gap-2 p-3 rounded-xl border bg-white text-center transition-all",
                              onboarding.businessType === b.id
                                ? "border-[#2D8653] ring-1 ring-[#2D8653]/30 shadow-sm"
                                : "border-border hover:border-[#2D8653]/40"
                            )}
                          >
                            <b.icon className="w-6 h-6" strokeWidth={1.5} style={{ color: "#2D8653" }} />
                            <span className="text-xs font-medium text-foreground">{b.label}</span>
                            {onboarding.businessType === b.id && <Check className="w-3.5 h-3.5" style={{ color: "#2D8653" }} />}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-foreground">{t("onboarding.country")}</Label>
                      <Select
                        value={selectedCountry}
                        onValueChange={(val) => {
                          setSelectedCountry(val);
                          update("city", ""); // reset city when country changes
                          update("country", val);
                        }}
                      >
                        <SelectTrigger className="mt-1.5 h-11">
                          <SelectValue placeholder={t("onboarding.selectCountry")} />
                        </SelectTrigger>
                        <SelectContent className="max-h-64">
                          {LOCATION_DATA.map(c => (
                            <SelectItem key={c.country} value={c.country}>
                              {c.flag} {c.country}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {selectedCountry && (
                      <div>
                        <Label className="text-sm font-medium text-foreground">{t("onboarding.city")}</Label>
                        <Select
                          value={onboarding.city}
                          onValueChange={(val) => update("city", val)}
                        >
                          <SelectTrigger className="mt-1.5 h-11">
                            <SelectValue placeholder={getPlaceholderForCountry(selectedCountry)} />
                          </SelectTrigger>
                          <SelectContent className="max-h-64">
                            {getCitiesForCountry(selectedCountry).map(city => (
                            <SelectItem key={city.name} value={city.name} disabled={city.comingSoon}>
                              <span className="flex items-center gap-2">
                                <span>{city.name}{city.state ? `, ${city.state}` : ""}</span>
                                 {city.comingSoon && (
                                  <Badge variant="outline" className="text-[9px] px-1.5 py-0 ml-1 text-muted-foreground">
                                    {t("onboarding.comingSoon")}
                                  </Badge>
                                )}
                              </span>
                            </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {/* City-specific warnings intentionally hidden during onboarding —
                            shown later in Permits & Licenses where they're actionable. */}
                      </div>
                    )}
                    {isManaging && (
                      <>
                        <div>
                          <Label className="text-sm font-medium text-foreground">How long have you been open?</Label>
                          <Select
                            value={onboarding.monthsOpen || ""}
                            onValueChange={(val) => update("monthsOpen", val)}
                          >
                            <SelectTrigger className="mt-1.5 h-11">
                              <SelectValue placeholder="Select time open" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0-6">0–6 months</SelectItem>
                              <SelectItem value="6-12">6–12 months</SelectItem>
                              <SelectItem value="1-2">1–2 years</SelectItem>
                              <SelectItem value="2+">2+ years</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-foreground">Number of seats / covers</Label>
                          <Input
                            type="number"
                            min={0}
                            inputMode="numeric"
                            placeholder="e.g. 60"
                            className="mt-1.5 h-11"
                            value={onboarding.seats || ""}
                            onChange={(e) => update("seats", parseInt(e.target.value) || 0)}
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-foreground">Approximate monthly revenue</Label>
                          <Input
                            type="number"
                            min={0}
                            inputMode="numeric"
                            placeholder="e.g. 85000"
                            className="mt-1.5 h-11"
                            value={onboarding.monthlyRevenueEstimate || ""}
                            onChange={(e) => update("monthlyRevenueEstimate", parseInt(e.target.value) || 0)}
                          />
                          <p className="mt-1 text-xs text-muted-foreground">In your local currency. Used to estimate your operations health.</p>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-foreground">Biggest challenge right now</Label>
                          <Select
                            value={onboarding.biggestChallenge || ""}
                            onValueChange={(val) => update("biggestChallenge", val)}
                          >
                            <SelectTrigger className="mt-1.5 h-11">
                              <SelectValue placeholder="Pick your top challenge" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="labor">Labor costs</SelectItem>
                              <SelectItem value="food">Food costs</SelectItem>
                              <SelectItem value="turnover">Staff turnover</SelectItem>
                              <SelectItem value="compliance">Compliance</SelectItem>
                              <SelectItem value="revenue">Revenue growth</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Step 1 — Goal */}
              {step === 1 && (
                <div>
                  <h2 className="font-heading text-2xl sm:text-3xl font-medium text-foreground">{t("onboarding.goal")}</h2>
                  <p className="mt-2 text-muted-foreground text-sm">{t("onboarding.goalDesc")}</p>
                  <div className="mt-8 space-y-3">
                    {goalOptions.map(g => (
                      <motion.button
                        key={g.id}
                        whileTap={{ scale: 0.99 }}
                        onClick={() => update("goal", g.id)}
                        className={cn(
                          "w-full p-5 rounded-xl border text-left transition-all flex items-start gap-4",
                          onboarding.goal === g.id
                            ? "border-primary bg-primary/5 ring-1 ring-primary/20 shadow-sm"
                            : "border-border bg-card hover:border-primary/30"
                        )}
                      >
                        
                        <div className="flex-1">
                          <div className="font-medium text-foreground">{g.label}</div>
                          <div className="text-sm text-muted-foreground mt-0.5">{g.desc}</div>
                        </div>
                        {onboarding.goal === g.id && (
                          <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center shrink-0 mt-1">
                            <Check className="w-3.5 h-3.5 text-primary-foreground" />
                          </div>
                        )}
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 3 — Seating capacity / Room configuration */}
              {step === 3 && !isRoomDriven && (
                <div>
                  <h2 className="font-heading text-2xl sm:text-3xl font-medium text-foreground">Seating capacity</h2>
                  <p className="mt-2 text-muted-foreground text-sm">
                    Tell us how many seats you plan to have. This drives revenue projections, staffing needs, and space recommendations.
                  </p>
                  <div className="mt-8 space-y-5">
                    <div>
                      <Label className="text-sm font-medium text-foreground">Indoor seats</Label>
                      <Input
                        type="number"
                        min={0}
                        inputMode="numeric"
                        placeholder="e.g. 40"
                        className="mt-1.5 h-11"
                        value={onboarding.indoorSeats || ""}
                        onChange={(e) => update("indoorSeats", parseInt(e.target.value) || 0)}
                      />
                      <p className="mt-1 text-xs text-muted-foreground">Tables, booths, and chairs inside the venue.</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-foreground">Outdoor seats</Label>
                      <Input
                        type="number"
                        min={0}
                        inputMode="numeric"
                        placeholder="e.g. 16"
                        className="mt-1.5 h-11"
                        value={onboarding.outdoorSeats || ""}
                        onChange={(e) => update("outdoorSeats", parseInt(e.target.value) || 0)}
                      />
                      <p className="mt-1 text-xs text-muted-foreground">Patio, terrace, sidewalk, or rooftop seats.</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-foreground">Bar seats</Label>
                      <Input
                        type="number"
                        min={0}
                        inputMode="numeric"
                        placeholder="e.g. 8"
                        className="mt-1.5 h-11"
                        value={onboarding.barSeats || ""}
                        onChange={(e) => update("barSeats", parseInt(e.target.value) || 0)}
                      />
                      <p className="mt-1 text-xs text-muted-foreground">Stools at the bar or counter.</p>
                    </div>

                    <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 flex items-center justify-between">
                      <div>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Total capacity</div>
                        <div className="mt-0.5 text-xs text-muted-foreground">Auto-calculated from the fields above.</div>
                      </div>
                      <div className="text-3xl font-medium text-primary tabular-nums">
                        {totalCapacity}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3 — Hotel / B&B room configuration */}
              {step === 3 && isRoomDriven && (
                <div>
                  <h2 className="font-heading text-2xl sm:text-3xl font-medium text-foreground">Room configuration</h2>
                  <p className="mt-2 text-muted-foreground text-sm">
                    Tell us about your guest rooms. This drives revenue projections, housekeeping needs, and front-office staffing.
                  </p>
                  <div className="mt-8 space-y-5">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium text-foreground">Room types</Label>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={addRoomType}
                          disabled={guestRooms.length >= ROOM_TYPE_OPTIONS.length}
                          className="gap-1 h-8"
                        >
                          <Plus className="w-3.5 h-3.5" /> Add type
                        </Button>
                      </div>

                      {guestRooms.length === 0 && (
                        <div className="rounded-xl border border-dashed border-border bg-muted/20 p-6 text-center">
                          <p className="text-sm text-muted-foreground">
                            No room types yet. Click <span className="font-medium text-foreground">Add type</span> to start.
                          </p>
                        </div>
                      )}

                      {guestRooms.map((room, idx) => {
                        const usedTypes = new Set(
                          guestRooms.filter((_, i) => i !== idx).map(r => r.type)
                        );
                        const rowMonthlyRev = Math.round(
                          (Number(room.quantity) || 0) *
                          (Number(room.adr) || 0) *
                          ((Number(room.occupancy) || 0) / 100) *
                          30
                        );
                        return (
                          <div
                            key={idx}
                            className="rounded-xl border border-border bg-card p-3 space-y-3"
                          >
                            {/* Row 1: Type + Qty + Remove */}
                            <div className="flex items-center gap-2">
                              <div className="flex-1">
                                <Select
                                  value={room.type}
                                  onValueChange={(val) => updateRoom(idx, { type: val })}
                                >
                                  <SelectTrigger className="h-10">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {ROOM_TYPE_OPTIONS.map(opt => (
                                      <SelectItem
                                        key={opt.type}
                                        value={opt.type}
                                        disabled={usedTypes.has(opt.type) && opt.type !== room.type}
                                      >
                                        {opt.type}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="w-20">
                                <Input
                                  type="number"
                                  min={0}
                                  inputMode="numeric"
                                  placeholder="Qty"
                                  className="h-10 text-center"
                                  value={room.quantity || ""}
                                  onChange={(e) => updateRoom(idx, { quantity: parseInt(e.target.value) || 0 })}
                                />
                              </div>
                              <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                onClick={() => removeRoom(idx)}
                                className="h-9 w-9 text-muted-foreground hover:text-destructive"
                                aria-label={`Remove ${room.type}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>

                            {/* Row 2: ADR + Occupancy */}
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-[11px] text-muted-foreground">ADR ({cs}/night)</Label>
                                <Input
                                  type="number"
                                  min={0}
                                  inputMode="decimal"
                                  placeholder="e.g. 150"
                                  className="mt-1 h-9"
                                  value={room.adr || ""}
                                  onChange={(e) => updateRoom(idx, { adr: parseFloat(e.target.value) || 0 })}
                                />
                              </div>
                              <div>
                                <Label className="text-[11px] text-muted-foreground">Occupancy (%)</Label>
                                <Input
                                  type="number"
                                  min={0}
                                  max={100}
                                  inputMode="numeric"
                                  placeholder="e.g. 65"
                                  className="mt-1 h-9"
                                  value={room.occupancy || ""}
                                  onChange={(e) => updateRoom(idx, { occupancy: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)) })}
                                />
                              </div>
                            </div>

                            {/* Per-row revenue projection */}
                            {rowMonthlyRev > 0 && (
                              <div className="text-[11px] text-muted-foreground flex items-center justify-between pt-1 border-t border-border/60">
                                <span>Projected monthly revenue</span>
                                <span className="font-medium text-foreground tabular-nums">
                                  {cs}{rowMonthlyRev.toLocaleString()}
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Total room count</div>
                          <div className="mt-0.5 text-xs text-muted-foreground">Auto-calculated from the room types above.</div>
                        </div>
                        <div className="text-3xl font-medium text-primary tabular-nums">
                          {totalRooms}
                        </div>
                      </div>
                      {projectedMonthlyRoomRevenue > 0 && (
                        <div className="flex items-center justify-between pt-3 border-t border-primary/20">
                          <div>
                            <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Projected monthly room revenue</div>
                            <div className="mt-0.5 text-xs text-muted-foreground">Rooms × ADR × occupancy × 30 days.</div>
                          </div>
                          <div className="text-2xl font-medium text-foreground tabular-nums">
                            {cs}{Math.round(projectedMonthlyRoomRevenue).toLocaleString()}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4 — Hotel outlets (hotels only) */}
              {step === 4 && hasOutletsStep && (
                <div>
                  <h2 className="font-heading text-2xl sm:text-3xl font-medium text-foreground">Your outlets</h2>
                  <p className="mt-2 text-muted-foreground text-sm">
                    Add the F&amp;B and service outlets inside your hotel. Each outlet feeds into staffing, permits, and budget.
                  </p>
                  <div className="mt-8 space-y-5">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium text-foreground">Outlets</Label>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={addOutlet}
                          className="gap-1 h-8"
                        >
                          <Plus className="w-3.5 h-3.5" /> Add outlet
                        </Button>
                      </div>

                      {outlets.length === 0 && (
                        <div className="rounded-xl border border-dashed border-border bg-muted/20 p-6 text-center">
                          <p className="text-sm text-muted-foreground">
                            No outlets yet. Click <span className="font-medium text-foreground">Add outlet</span> to start.
                          </p>
                        </div>
                      )}

                      {outlets.map((outlet, idx) => (
                        <div
                          key={idx}
                          className="rounded-xl border border-border bg-card p-3 space-y-3"
                        >
                          {/* Row 1: Name + Remove */}
                          <div className="flex items-center gap-2">
                            <div className="flex-1">
                              <Input
                                placeholder="Outlet name (e.g. Sunset Grill)"
                                className="h-10"
                                value={outlet.name}
                                onChange={(e) => updateOutlet(idx, { name: e.target.value })}
                              />
                            </div>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              onClick={() => removeOutlet(idx)}
                              className="h-9 w-9 text-muted-foreground hover:text-destructive"
                              aria-label={`Remove ${outlet.name || "outlet"}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>

                          {/* Row 2: Type + Seats */}
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-[11px] text-muted-foreground">Type</Label>
                              <Select
                                value={outlet.type}
                                onValueChange={(val) => updateOutlet(idx, { type: val })}
                              >
                                <SelectTrigger className="mt-1 h-9">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {OUTLET_TYPE_OPTIONS.map(t => (
                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-[11px] text-muted-foreground">Seating capacity</Label>
                              <Input
                                type="number"
                                min={0}
                                inputMode="numeric"
                                placeholder="e.g. 60"
                                className="mt-1 h-9"
                                value={outlet.seats || ""}
                                onChange={(e) => updateOutlet(idx, { seats: parseInt(e.target.value) || 0 })}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 flex items-center justify-between">
                      <div>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Total covers</div>
                        <div className="mt-0.5 text-xs text-muted-foreground">Auto-calculated across all outlets.</div>
                      </div>
                      <div className="text-3xl font-medium text-primary tabular-nums">
                        {totalOutletCovers}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Plan step — index 4 normally, 5 for hotels (after outlets) */}
              {((!hasOutletsStep && step === 4) || (hasOutletsStep && step === 5)) && (
                <div>
                  <h2 className="font-heading text-2xl sm:text-3xl font-medium text-foreground">{t("onboarding.choosePlan")}</h2>
                  <p className="mt-2 text-muted-foreground text-sm">{t("onboarding.choosePlanDesc")}</p>
                  <div className="mt-8 space-y-3">
                    {planOptions.map(p => {
                      const Icon = p.icon;
                      return (
                        <motion.button
                          key={p.key}
                          whileTap={{ scale: 0.99 }}
                          onClick={() => update("selectedPlan", p.key)}
                          className={cn(
                            "w-full p-4 rounded-xl border text-left transition-all flex items-start gap-4 relative",
                            onboarding.selectedPlan === p.key
                              ? "border-primary bg-primary/5 ring-1 ring-primary/20 shadow-sm"
                              : "border-border bg-card hover:border-primary/30",
                            p.popular && "ring-2 ring-emerald-400/20"
                          )}
                        >
                          {p.popular && (
                            <span className="absolute -top-2.5 right-3 bg-emerald-500 text-white text-[10px] font-medium px-2 py-0.5 rounded-full">
                              {t("onboarding.popular")}
                            </span>
                          )}
                          <div className={cn(
                            "w-9 h-9 rounded-lg flex items-center justify-center shrink-0",
                            p.popular ? "bg-emerald-500/10" : "bg-primary/10"
                          )}>
                            <Icon className={cn("w-4 h-4", p.popular ? "text-emerald-500" : "text-primary")} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2">
                              <span className="font-heading font-medium text-foreground">{p.name}</span>
                              <span className="text-sm text-muted-foreground">
                                {p.price === 0 ? "" : `$${p.price}/mo`}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                              {p.features.map(f => (
                                <span key={f} className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Check className="w-3 h-3 text-primary" /> {f}
                                </span>
                              ))}
                            </div>
                          </div>
                          {onboarding.selectedPlan === p.key && (
                            <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center shrink-0 mt-1">
                              <Check className="w-3.5 h-3.5 text-primary-foreground" />
                            </div>
                          )}
                        </motion.button>
                      );
                    })}
                  </div>
                </div>
              )}

            </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Navigation — hidden on the completion screen */}
      {!completed && (
      <div className="container py-6 flex flex-col gap-2">
        <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={prev} className="gap-2 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> {t("back")}
        </Button>
        {(() => {
          const isFinalStep = isManaging ? step === 2 : step === FULL_STEPS - 1;
          const blocked = !canNext();
          return (
            <Button
              onClick={() => {
                if (blocked) {
                  setShowValidationHint(true);
                  return;
                }
                setShowValidationHint(false);
                next();
              }}
              aria-disabled={blocked}
              className={cn(
                "gap-2 transition-all duration-300",
                blocked && "opacity-60 cursor-not-allowed",
                isFinalStep && !blocked && "shadow-lg hover:shadow-xl hover:scale-[1.03] px-8 bg-gradient-to-r from-primary to-primary/90"
              )}
            >
              {step === 0 ? (
                <>{t("onboarding.letsGo")} <ArrowRight className="w-4 h-4" /></>
              ) : isFinalStep ? (
                <>{t("onboarding.launchDashboard")} <Sparkles className="w-4 h-4" /></>
              ) : (
                <>{t("onboarding.continue")} <ArrowRight className="w-4 h-4" /></>
              )}
            </Button>
          );
        })()}
        </div>
        <AnimatePresence>
          {showValidationHint && !canNext() && (
            <motion.p
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="text-xs text-destructive text-right"
              role="alert"
            >
              Please complete the required fields above to continue.
            </motion.p>
          )}
        </AnimatePresence>
      </div>
      )}
    </div>
  );
}
